import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  FetchHeaders,
  FormValues,
  IExecuteIntegrationTestRequestDto,
  IExecuteResultDto,
  IJSONSchema,
  RequestHeaders
} from './integration-test-modal.types';
import { formValueUtils } from './integration-test-modal.utils';

const HEADERS_FETCH_DEBOUNCE_MS = 300;

interface IUseIntegrationTestParams {
  schema: IJSONSchema;
  fetchHeaders: FetchHeaders;
  defaultValues?: FormValues;
  onTest?: (request: IExecuteIntegrationTestRequestDto) => Promise<IExecuteResultDto>;
}

interface IUseIntegrationTestReturn {
  formValues: FormValues;
  updateFormValue: (path: string, value: unknown) => void;
  resetForm: () => void;
  response: IExecuteResultDto | undefined;
  isLoading: boolean;
  executeTest: () => Promise<void>;
  requestBody: FormValues;
  isFormValid: boolean;
  missingRequiredFields: string[];
  headers: RequestHeaders;
}


export const useIntegrationTest = ({
  schema,
  fetchHeaders,
  defaultValues,
  onTest
}: IUseIntegrationTestParams): IUseIntegrationTestReturn => {
  const initialValues = useMemo((): FormValues => {
    const schemaDefaults = formValueUtils.buildDefaultValuesFromSchema(schema, schema.definitions);
    return formValueUtils.deepMerge(schemaDefaults, defaultValues || {});
  }, [schema, defaultValues]);

  const [formValues, setFormValues] = useState<FormValues>(initialValues);
  const [headers, setHeaders] = useState<RequestHeaders>({});
  const [response, setResponse] = useState<IExecuteResultDto | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const fetchHeadersRef = useRef(fetchHeaders);
  fetchHeadersRef.current = fetchHeaders;

  useEffect(() => {
    setFormValues(initialValues);
  }, [initialValues]);

  const updateFormValue = useCallback((path: string, value: unknown): void => {
    setFormValues((prev) => formValueUtils.setNestedValue(prev, path, value));
  }, []);

  const resetForm = useCallback((): void => {
    setFormValues(initialValues);
    setHeaders({});
    setResponse(undefined);
  }, [initialValues]);

  const cleanedPayload = useMemo(
    () => formValueUtils.removeEmptyValues(formValues),
    [formValues]
  );

  useEffect(() => {
    let isCancelled = false;

    const timeoutId = setTimeout(async () => {
      try {
        const updatedHeaders = await fetchHeadersRef.current(cleanedPayload);
        if (!isCancelled) {
          setHeaders(updatedHeaders);
        }
      } catch {
        
      }
    }, HEADERS_FETCH_DEBOUNCE_MS);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [cleanedPayload]);

  const validationResult = useMemo(() => {
    return formValueUtils.validateRequiredFields(
      schema.required,
      formValues,
      schema.properties,
      schema.definitions
    );
  }, [schema.required, schema.properties, schema.definitions, formValues]);

  const isFormValid = useMemo(() => validationResult.isValid, [validationResult.isValid]);

  const missingRequiredFields = useMemo(
    () => validationResult.missingFields,
    [validationResult.missingFields]
  );

  const executeTest = useCallback(async (): Promise<void> => {
    if (!onTest) {
      setResponse(undefined);
      return;
    }

    setIsLoading(true);
    try {
      const request: IExecuteIntegrationTestRequestDto = {
        headers,
        payload: cleanedPayload
      };
      const result = await onTest(request);
      setResponse(result);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setResponse({
        success: false,
        statusCode: 500,
        responseHeaders: {},
        responseBody: { error: errorMessage },
      });
    } finally {
      setIsLoading(false);
    }
  }, [cleanedPayload, headers, onTest]);

  return {
    formValues,
    updateFormValue,
    resetForm,
    response,
    isLoading,
    executeTest,
    requestBody: cleanedPayload,
    isFormValid,
    missingRequiredFields,
    headers
  };
};
